import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { Col, Input, Row } from 'reactstrap';
import classNames from 'classnames';
import LoadingPage from '../../LoadingPage';
import Pagination from '../../../../../shared/components/Pagination';
import useDebounce from '../../../../../shared/helpers/useDebounce';

/**
 * @author Renan Salustiano <renan.salustiano@ter.grupomarista.org.br>
 * @param name {string}
 * @param onChange {function}
 * @param onSelect {function}
 * @param customFilter {boolean}
 * @param value {number|string}
 * @param data {[] | null}
 * @param placeholder {string}
 * @param pagination {{currentPage: number, totalPages: number} | null}
 * @param loading {boolean}
 * @returns {JSX.Element}
 * @constructor
 */
const DropDownSearch = forwardRef(
    (
        {
            name,
            onChange,
            onSelect,
            customFilter,
            value = '',
            data = null,
            placeholder = '',
            pagination,
            loading = false,
        },
        ref,
    ) => {
        const [val, setVal] = useState(value || '');
        const [dataSource, setDataSource] = useState();
        const [inputText, setInputText] = useState();
        const [textToFind, setTextToFind] = useState();
        const refDatalist = useRef(null);
        const refInput = useRef(null);
        const [find, setFind] = useState(false);

        useEffect(() => {
            document.addEventListener('keydown', handleHideDropdown, true);
            document.addEventListener('click', handleClickOutside, true);
            return () => {
                document.removeEventListener(
                    'keydown',
                    handleHideDropdown,
                    true,
                );
                document.removeEventListener('click', handleClickOutside, true);
            };
        });

        useEffect(() => {
            setVal(value);
        }, [value]);

        useEffect(() => {
            if (data !== null) {
                setDataSource(data);

                if (val) {
                    const item = getItemById(val);
                    if (item) {
                        setInputText(item?.text);

                        if (item?.textToFind) {
                            setTextToFind(item?.textToFind);
                        }
                    }
                }
            }
        }, [data, val]);

        useEffect(() => {
            if (find) {
                if (!showDataList) {
                    setShowDatalist(true);
                }

                if (customFilter) {
                    debouncedChange(textToFind || inputText, 1);
                } else {
                    const regex = new RegExp(textToFind || value);
                    setDataSource(data.filter(item => regex.test(item.text)));
                }

                setFind(false);
            }
        }, [inputText]);

        /**
         *
         * @param value
         */
        const customFilterOnChange = value => {
            if (customFilter) {
                onChange(textToFind || value, 1);
            }
        };

        const [debouncedChange, clearDebounce] = useDebounce(
            customFilterOnChange,
            500,
        );

        const [showDataList, setShowDatalist] = useState(false);

        /**
         * Busca o item na lista pelo id
         * @param id
         * @returns {T}
         */
        const getItemById = id => {
            return data.find(item => item.id === id);
        };

        /**
         * Ao teclar enter
         * @param event
         */
        const handleHideDropdown = event => {
            if (event.key === 'Escape') {
                setShowDatalist(false);
            }
        };

        /**
         * Click fora da lista
         * @param event
         */
        const handleClickOutside = event => {
            if (event.target === refInput.current) {
                return;
            }

            if (
                refDatalist.current &&
                !refDatalist.current.contains(event.target)
            ) {
                if (!val) {
                    setInputText('');
                }
                setShowDatalist(false);
            }
        };
        /**
         * Ao clicar na input
         * @param e
         */
        const handleClickInput = e => {
            if (!val) {
                if (showDataList) {
                    setInputText('');
                    return;
                }
            }

            onChange(textToFind || inputText);
            setShowDatalist(!showDataList);
        };

        /**
         * Ao alterar input
         * @param e
         */
        const handleOnChangeInput = e => {
            const { value } = e.target;
            /**
             * Se houver valor selecionado limpar a input
             */
            if (val) {
                const lastChar = value.at(-1);
                setTextToFind(null);
                setInputText(lastChar);
            } else {
                setTextToFind(value);
                setInputText(value);
            }

            setVal(null);
            setFind(true);
            if (onSelect) onSelect(null);
        };
        /**
         * Ao selecionar item
         * @param item
         */
        const handleOnSelect = item => {
            if (onSelect) {
                onSelect(item);
            }

            if (val !== item?.id) {
                setVal(item?.id);
                setInputText(item?.text);
                setTextToFind(item?.textToFind);
                setShowDatalist(false);
            }
        };

        return (
            <div className="dropDownSearch mb-2 mb-md-2">
                <Col md="12 px-0">
                    <Row className="position-relative">
                        <Col md="12 px-0">
                            <Input
                                innerRef={element => {
                                    refInput.current = element;
                                    if (ref) ref.current = element;
                                }}
                                className=""
                                type="text"
                                onKeyDown={e => {
                                    const backspaceClicked = e.keyCode === 8;
                                    if (backspaceClicked && val) {
                                        clearDebounce();
                                        onChange('', 1);
                                        setVal(null);
                                        setTextToFind(null);
                                        setInputText(null);
                                        if (onSelect) onSelect(null);
                                    }
                                }}
                                onChange={e => handleOnChangeInput(e)}
                                onClick={e => handleClickInput()}
                                onFocus={handleClickInput}
                                value={inputText || ''}
                                placeholder={
                                    loading === true
                                        ? 'Carregando ...'
                                        : placeholder
                                }
                            />
                        </Col>

                        <Input name={name} type="hidden" value={val || ''} />
                        {!!textToFind && (
                            <Input type="hidden" value={textToFind || ''} />
                        )}

                        {!!showDataList && (
                            <div ref={refDatalist} className="dataList col-12">
                                {(!!loading && (
                                    <Col md="12" className="bkbranco">
                                        <LoadingPage />
                                    </Col>
                                )) || (
                                    <>
                                        <ul className="sombra">
                                            {!!dataSource?.length > 0 &&
                                                dataSource.map(
                                                    (item, index) => (
                                                        <li
                                                            key={
                                                                item.key ??
                                                                index
                                                            }
                                                            onClick={() =>
                                                                handleOnSelect(
                                                                    item,
                                                                )
                                                            }
                                                            className={classNames(
                                                                'item pad2 preto70 bkbranco col-12  list',
                                                                {
                                                                    selectedItem:
                                                                        item?.id ===
                                                                        val,
                                                                },
                                                            )}
                                                        >
                                                            {item?.text}
                                                        </li>
                                                    ),
                                                )}
                                        </ul>

                                        {pagination?.totalPages > 1 && (
                                            <Pagination
                                                genericCallback={page => {
                                                    onChange(
                                                        textToFind || inputText,
                                                        page,
                                                    );
                                                }}
                                                {...pagination}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </Row>
                </Col>
            </div>
        );
    },
);
/**
 * Monta os dados na estrutura requerida pelo componente
 * @param data {[]}
 * @param id
 * @param key
 * @param value
 * @param displayValue
 * @returns {{displayValue: *, id: *, value: *, key: *}[]}
 */
export const makeDataSource = (data, { id, key, text, textToFind }) => {
    return data.map(item => {
        try {
            const valueId = typeof id === 'function' ? id(item) : item[id];
            const valueKey = typeof key === 'function' ? key(item) : item[key];
            const valueText =
                typeof text === 'function' ? text(item) : item[text];
            const valueTextToFind =
                typeof textToFind === 'function'
                    ? textToFind(item)
                    : item[textToFind];

            return {
                id: valueId,
                key: valueKey,
                text: valueText,
                textToFind: valueTextToFind,
                originalObject: item,
            };
        } catch (e) {
            return {};
        }
    });
};

export default DropDownSearch;
